import {useMemo} from 'react';
import {getPrefixedLogger} from '@netx/core-next/instance/logger';
import deepmerge from 'deepmerge';
import {UppyStateStore} from '../store';
import type {AnyUppyFile, UppyFileMeta} from '../types';
import {useUpload, UseUploadParams} from './useUpload';

const logger = getPrefixedLogger('useSingleFileUpload');

const uppyLogger = {
	debug: logger.debug.bind(logger),
	error: logger.error.bind(logger),
	warn: logger.warn.bind(logger),
};

const defaultOptions: UseUploadParams<any, any> = {
	xhrOptions: {
		limit: 1,
	},
	tusOptions: {
		limit: 1,
		// no retry, at least for now
		// we currently get some mixed results back with misconfigs
		// sometimes a 413 and sometimes a total network error (hang up)
		onShouldRetry: () => false,
	},
	uppyOptions: {
		logger: uppyLogger,
	},
	useThumbnail: true,
};

export type UseSingleFileUploadParams<
	TMeta extends UppyFileMeta = UppyFileMeta,
	TStore extends UppyStateStore<TMeta> = UppyStateStore<TMeta>,
> = UseUploadParams<TMeta, TStore>;

export const useSingleFileUpload = <
	TMeta extends UppyFileMeta = UppyFileMeta,
	TStore extends UppyStateStore<TMeta> = UppyStateStore<TMeta>,
>(
	props: UseUploadParams<TMeta, TStore>,
) => {
	const options = useMemo(
		() => deepmerge<UseUploadParams<TMeta, TStore>>(defaultOptions, props),
		[props],
	);
	return useUpload(options);
};

// Typescript cannot infer `ReturnType` of a generic, so you must roll your own
class InferWrapper<
	TMeta extends UppyFileMeta = UppyFileMeta,
	TStore extends UppyStateStore<TMeta> = UppyStateStore<TMeta>,
> {
	mediate = (...args: any[]) => useUpload<TMeta, TStore>(...args);
}

export type UseSingleFileUploadType<
	TMeta extends UppyFileMeta = UppyFileMeta,
	TStore extends UppyStateStore<TMeta> = UppyStateStore<TMeta>,
> = ReturnType<InferWrapper<TMeta, TStore>['mediate']>;
